import BusinessIcon from '@mui/icons-material/Business';
import { Box, Chip } from '@mui/material';
import React, { useState } from 'react';
import { InvestmentAllocation, InvestmentEntity } from '../../models/InvestmentEntity';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import StyledModal from '../styles/StyledModal';
import {
  StyledAvatar,
  StyledCard,
  StyledCardContent,
  StyledHeading,
  StyledTypography,
} from '../styles/styles';

interface EntitySelectionStepProps {
  entities: InvestmentEntity[];
  onEntitySelected: (entity: InvestmentEntity) => void;
  onCreateNew: () => void;
  initialSelection?: { selectedEntityId?: string; createNew?: boolean }; // Add initial selection support
  currentAllocations: InvestmentAllocation[];
  totalAllocations: number;
  remainingAllocations: number;
}

export const EntitySelectionStep: React.FC<EntitySelectionStepProps> = ({
  entities,
  onEntitySelected,
  onCreateNew,
  currentAllocations,
  totalAllocations,
  remainingAllocations,
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [entityToConfirm, setEntityToConfirm] = useState<InvestmentEntity | null>(null);
  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      TRUST: 'Trust',
      COMPANY: 'Company',
      FUND: 'Fund',
      PARTNERSHIP: 'Partnership',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getEntityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      INDIVIDUAL: '#4CAF50',
      TRUST: '#2196F3',
      COMPANY: '#FF9800',
      FUND: '#9C27B0',
      PARTNERSHIP: '#F44336',
      OTHER: '#607D8B',
    };
    return colors[type] || '#607D8B';
  };

  const getExistingAllocation = (entityId: string) => {
    return currentAllocations.find((allocation) => allocation.investmentEntityId === entityId);
  };

  const handleEntityClick = (entity: InvestmentEntity) => {
    const existingAllocation = getExistingAllocation(entity.id);

    if (existingAllocation) {
      // Show confirmation modal for entities with existing allocations
      setEntityToConfirm(entity);
      setConfirmationModalOpen(true);
    } else {
      // Direct selection for entities without allocations
      onEntitySelected(entity);
    }
  };

  const handleConfirmSelection = () => {
    if (entityToConfirm) {
      onEntitySelected(entityToConfirm);
    }
    setConfirmationModalOpen(false);
    setEntityToConfirm(null);
  };

  const handleCancelSelection = () => {
    setConfirmationModalOpen(false);
    setEntityToConfirm(null);
  };

  return (
    <>
      <StyledCard tone="tone1">
        <StyledCardContent sx={{ py: 3 }}>
          <StyledTypography sx={{ mb: 3 }}>
            Current Allocations: {ExtendedLiquid.currencyMillions(totalAllocations, false)}
          </StyledTypography>
          <StyledTypography sx={{ mb: 3 }}>
            Remaining Allocations: {ExtendedLiquid.currencyMillions(remainingAllocations, false)}
          </StyledTypography>
          <StyledHeading headerVariant="heading2" sx={{ mb: 3 }}>
            Choose an Investment Entity
          </StyledHeading>

          {entities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <StyledTypography sx={{ mb: 3 }}>
                No existing investment entities found where you are the owner.
              </StyledTypography>
              <StyledButton variant="contained" onClick={onCreateNew} size="large">
                Create New Entity
              </StyledButton>
            </Box>
          ) : (
            <>
              <StyledTypography sx={{ mb: 3 }}>
                Select an existing entity or create a new one:
              </StyledTypography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                  mb: 4,
                }}
              >
                {entities.map((entity) => {
                  const existingAllocation = getExistingAllocation(entity.id);
                  return (
                    <Box
                      key={entity.id}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover .action-overlay': {
                          opacity: 1,
                          visibility: 'visible',
                        },
                      }}
                    >
                      <StyledCard
                        tone="tone1"
                        sx={{
                          padding: '12px',
                          height: '120px',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'translateY(-2px)',
                          },
                        }}
                        onClick={() => handleEntityClick(entity)}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                          }}
                        >
                          <StyledAvatar
                            tone="tone1"
                            sx={{
                              width: '32px',
                              height: '32px',
                              fontSize: '0.8rem',
                              backgroundColor: getEntityTypeColor(entity.type),
                            }}
                          >
                            <BusinessIcon sx={{ fontSize: '1rem' }} />
                          </StyledAvatar>
                          <Chip
                            label={getEntityTypeLabel(entity.type)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.6rem',
                              height: '20px',
                              borderColor: getEntityTypeColor(entity.type),
                              color: getEntityTypeColor(entity.type),
                            }}
                          />
                        </Box>

                        <Box sx={{ flex: 1, mt: 1 }}>
                          <StyledTypography
                            fontWeight={700}
                            tone="tone1"
                            sx={{
                              fontSize: '0.85rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {entity.name}
                          </StyledTypography>
                          {entity.description && (
                            <StyledTypography
                              variant="body2"
                              tone="tone1"
                              sx={{
                                fontSize: '0.7rem',
                                opacity: 0.8,
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {entity.description}
                            </StyledTypography>
                          )}
                          {existingAllocation && (
                            <StyledTypography
                              sx={{
                                fontSize: '0.7rem',
                                opacity: 0.8,
                                mt: 0.5,
                              }}
                            >
                              Current Allocation:{' '}
                              {ExtendedLiquid.currencyMillions(
                                Number(existingAllocation.allocatedAmount),
                                false,
                              )}
                            </StyledTypography>
                          )}
                        </Box>
                      </StyledCard>

                      {/* Hover Overlay */}
                      <Box
                        className="action-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0,0,0,0.85)',
                          borderRadius: 'inherit',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          opacity: 0,
                          visibility: 'hidden',
                          transition: 'opacity 0.2s ease',
                          zIndex: 10,
                        }}
                      >
                        <StyledTypography
                          sx={{
                            color: 'white',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            marginBottom: '8px',
                            textAlign: 'center',
                            px: 1,
                          }}
                        >
                          {entity.name}
                        </StyledTypography>
                        <StyledButton
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEntityClick(entity);
                          }}
                          sx={{
                            fontSize: '0.6rem',
                            py: 0.5,
                            px: 1.5,
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            color: 'rgba(0,0,0,0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,1)',
                              color: 'rgba(0,0,0,1)',
                            },
                          }}
                        >
                          {existingAllocation ? 'Change Allocation' : 'Select Entity'}
                        </StyledButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  textAlign: 'center',
                  borderTop: 1,
                  borderColor: 'divider',
                  pt: 3,
                }}
              >
                <StyledTypography sx={{ mb: 2 }}>Don't see the entity you need?</StyledTypography>
                <StyledButton variant="outlined" onClick={onCreateNew}>
                  Create New Entity
                </StyledButton>
              </Box>
            </>
          )}
        </StyledCardContent>
      </StyledCard>

      {/* Confirmation Modal for Existing Allocations */}
      <StyledModal
        open={confirmationModalOpen}
        onClose={handleCancelSelection}
        title="Confirm Allocation Change"
      >
        <Box sx={{ p: 2 }}>
          {entityToConfirm && (
            <>
              <StyledTypography sx={{ mb: 2 }}>
                You are about to change the allocation for <strong>{entityToConfirm.name}</strong>.
              </StyledTypography>

              <StyledTypography sx={{ mb: 3, color: 'text.secondary' }}>
                Current allocation:{' '}
                {ExtendedLiquid.currencyMillions(
                  Number(getExistingAllocation(entityToConfirm.id)?.allocatedAmount ?? 0),
                  false,
                )}
              </StyledTypography>
            </>
          )}

          <StyledTypography sx={{ mb: 3 }}>
            This will allow you to modify the allocation amount. Are you sure you want to proceed?
          </StyledTypography>

          <StyledButtonGroup justifyContent="center">
            <StyledButton variant="outlined" onClick={handleCancelSelection}>
              Cancel
            </StyledButton>
            <StyledButton variant="contained" onClick={handleConfirmSelection}>
              Continue
            </StyledButton>
          </StyledButtonGroup>
        </Box>
      </StyledModal>
    </>
  );
};
